import type { RecentSearch } from '../types/recent-search';

/**
 * Filters recent searches by user input and ranks results by relevance.
 *
 * Supports multi-token queries: "use hook" matches "useState hook" because
 * both tokens appear in the string (order-independent). Matching is
 * case-insensitive.
 *
 * Results are ranked prefix-first: items where the search string starts with
 * the full normalized input are ranked above pure substring matches. Within each
 * tier, the original (recency) order is preserved.
 *
 * When `query` is empty or blank, the first `limit` items are returned
 * unmodified — preserving the current "show most recent" behavior.
 */
export function filterRecentSearches(
  searches: RecentSearch[],
  query: string,
  limit: number
): RecentSearch[] {
  const tokens = query.trim().toLowerCase().split(/\s+/);

  if (tokens[0] === '') {
    return searches.slice(0, limit);
  }

  const joinedTokens = tokens.join(' ');
  const prefixMatches: RecentSearch[] = [];
  const substringMatches: RecentSearch[] = [];

  for (const search of searches) {
    const lower = search.query.toLowerCase();

    // AND semantics: every token must appear somewhere in the string.
    // We assume that users recall past searches by fragments (e.g. "auth python"
    // to find "repo:code-intelligence language:Python auth"), so partial recall
    // across tokens are matched rather than matching multiple tokens individually
    // across different recent search queries.
    if (!tokens.every(token => lower.includes(token))) {
      continue;
    }

    // Prefix matches are prioritized to capture users re-typing exact recent searches.
    if (lower.startsWith(joinedTokens)) {
      prefixMatches.push(search);
    } else {
      substringMatches.push(search);
    }
  }

  // Prefix matches first, then substring. Recency order is preserved within
  // each tier since `searches` arrives sorted by recency from storage.
  return [...prefixMatches, ...substringMatches].slice(0, limit);
}
